import { seededRandom, seededShuffle } from './random';
import { pointInFieldRect, type FieldEventZone } from './field';
import type { FateMapDefinition, RaceEventType, ScheduledRaceEvent } from './types';

const EVENT_COPY: Record<RaceEventType, { label: string; description: string }> = {
  gate: { label: '게이트 전환', description: '중앙 통로가 좁아지고 양옆 길이 열립니다.' },
  boost: { label: '부스터 웨이브', description: '가속 구역이 뒤쪽부터 켜집니다.' },
  reverse: { label: '회전 장치 역주행', description: '회전 장치의 방향이 뒤집힙니다.' },
  slow: { label: '슬로우 존 이동', description: '감속 구역이 새로운 위치로 이동합니다.' },
  'belt-reverse': { label: '컨베이어 역주', description: '벨트 세 줄이 잠시 거꾸로 돕니다.' },
  'gate-close': { label: '게이트 일괄 폐쇄', description: '압력 게이트 세 개가 동시에 닫힙니다.' },
  'shortcut-open': { label: 'C레인 급행 개방', description: '대기 중이던 아래쪽 C레인이 짧고 빠른 급행 통로로 열립니다.' },
  'disk-reverse': { label: '원판 역회전', description: '회전 원판 두 개가 반대로 돕니다.' },
  'shutter-open': { label: '셔터 조기 개방', description: '중앙 셔터가 예정에 없던 타이밍에 열립니다.' },
  'boost-ring': { label: '외곽 부스터 링', description: '바깥 링의 흐름이 크게 빨라집니다.' },
  'seesaw-free': { label: '시소 잠금 해제', description: '시소가 복원력을 잃고 자유롭게 흔들립니다.' },
  'tilt-reverse': { label: '광장 경사 반전', description: '광장 바닥의 흐름이 반대로 뒤집힙니다.' },
  'wall-open': { label: '중앙 벽 개방', description: '막혀 있던 벽 게이트가 열려 지름길이 생깁니다.' },
  'gravity-early': { label: '중력 조기 전환', description: '방 B의 중력이 예고 없이 아래로 바뀝니다.' },
  'room-reverse': { label: '방 통째 반전', description: '방 C의 역중력이 잠시 정방향으로 뒤집힙니다.' },
  'transfer-delay': { label: '전환실 지연', description: '전환 게이트가 늦게 열려 팩이 압축됩니다.' },
  'flipper-frenzy': { label: '플리퍼 동시 가동', description: '플리퍼 두 개가 쉬지 않고 휘두릅니다.' },
  'drop-close': { label: '드롭 존 폐쇄', description: '세 갈래 중 하나가 막혀 남은 길로 몰립니다.' },
  'pin-sink': { label: '핀 승강', description: '핀 마지막 줄이 바닥으로 가라앉습니다.' },
  'lift-express': { label: '리프트 급행', description: '모든 리프트가 잠시 빠르게 왕복합니다.' },
  'early-dump': { label: '대형 조기 배출', description: '대형 리프트가 정원을 채우기 전에 쏟아냅니다.' },
  'rotor-stop': { label: '관문 개방폭 확대', description: '통로 회전봉이 잠시 멈춰 길이 넓어집니다.' },
  'patch-expand': { label: '역류 확산', description: '역주행 패치가 세 레인 전체로 넓어집니다.' },
  'gate-frenzy': { label: '게이트 폭주', description: '파도 게이트가 잠시 빠르게 여닫힙니다.' },
  'cut-open': { label: '나선 관통', description: '나선 컷 게이트 세 개가 동시에 열립니다.' },
  'boost-surge': { label: '부스터 과충전', description: '직선 부스터가 1.8배 출력으로 뿜어냅니다.' },
  'rotor-reverse': { label: '원반 역주', description: '4날개 회전봉이 감속한 뒤 반대 방향으로 돕니다.' },
  'gravity-surge': { label: '중력 폭주', description: '타워 전체의 중력이 1.5배 강해지고 낙하 속도 상한이 잠시 높아집니다.' },
  'gravity-turn': { label: '중력 급회전', description: '연결 회랑에서 중력 방향이 더 빠르게 바뀝니다. 결승 아레나는 그대로 유지됩니다.' },
  'drum-reverse': { label: '드럼 역주', description: 'C·H 사발의 회전 드럼이 감속한 뒤 반대 방향으로 돕니다.' },
  'slow-ring-release': { label: '족쇄 해제', description: 'G 사발의 노란 슬로우 링이 잠시 꺼져 마블이 빠르게 빠져나갑니다.' },
  'circuit-cut-open': { label: '2랩 지름길 개방', description: '2랩 마블에게 인필드 급행 통로가 활짝 열립니다. 1랩 마블은 바깥 링을 돌아요.' },
  'circuit-pin-sink': { label: '페그 붕괴', description: '아래 직선의 페그 세 개가 가라앉아 추월 공간이 열립니다.' },
};

const MINIMUM_EVENT_GAP = 6;

function scheduleFieldEvents(map: FateMapDefinition, seed: string, resultTime: number): ScheduledRaceEvent[] {
  const specs = map.field?.events ?? [];
  const random = seededRandom(seed, 'events');
  const shuffled = seededShuffle(specs, seed, 'event-order');
  const desiredCount = random() < .5 ? 2 : 3;
  const required = map.field?.eventScheduling ? specs.filter((spec) => spec.required) : [];
  const chosen = [...required, ...shuffled.filter((spec) => !required.includes(spec)).slice(0, Math.max(0, desiredCount - required.length))]
    .sort((left, right) => (left.window[0] + left.window[1]) - (right.window[0] + right.window[1]));
  const events: ScheduledRaceEvent[] = [];
  if (map.field?.eventScheduling === 'occupancy') {
    // 후보만 추첨한다. 예전 목표 완주 시간/창에 억지로 맞추지 않는다.
    return chosen.map((spec, index) => ({
      id: `event-${index + 1}`, type: spec.type, ...EVENT_COPY[spec.type],
      announcesAt: 1.5, startsAt: 2.05, duration: spec.duration,
      trigger: { minProgress: .08, maxProgress: .94, ...spec.trigger, occupancy: true }, rampSeconds: .25,
    }));
  }
  if (map.field?.eventScheduling === 'live') {
    return chosen.map((spec, index) => {
      const start = spec.window[0] + random() * (spec.window[1] - spec.window[0]);
      return { id: `event-${index + 1}`, type: spec.type, ...EVENT_COPY[spec.type], announcesAt: start - 2, startsAt: start, duration: spec.duration, trigger: spec.trigger, latestStart: spec.window[1], rampSeconds: .5 };
    });
  }
  const minimumGap = Math.max(MINIMUM_EVENT_GAP, map.field?.eventMinGapSeconds ?? 0);
  const finalGuard = Math.max(3, map.field?.eventFinalGuardSeconds ?? 0);
  let earliestNext = 0;
  chosen.forEach((spec, index) => {
    const remaining = chosen.length - index - 1;
    const latestForThis = resultTime - finalGuard - remaining * minimumGap;
    const preferred = spec.window[0] + random() * Math.max(0, spec.window[1] - spec.window[0]);
    // 남은 이벤트 슬롯을 먼저 확보해 안내대로 항상 2~3회 발생시킨다.
    const start = Math.max(earliestNext, Math.min(preferred, latestForThis));
    earliestNext = start + minimumGap;
    events.push({
      id: `event-${index + 1}`,
      type: spec.type,
      ...EVENT_COPY[spec.type],
      announcesAt: start - 2,
      startsAt: start,
      duration: spec.duration,
    });
  });
  return events;
}

export function scheduleRaceEvents(
  map: FateMapDefinition,
  seed: string,
  enabled: boolean,
  resultTime: number,
): ScheduledRaceEvent[] {
  if (!enabled) return [];
  if (map.field) return scheduleFieldEvents(map, seed, resultTime);
  const random = seededRandom(seed, 'events');
  const types = seededShuffle(map.eventPool, seed, 'event-order');
  const desiredCount = random() < .5 ? 2 : 3;
  const starts = [9 + random() * 2, 18.5 + random() * 2.5, 23.8 + random() * .7];
  return starts
    .slice(0, desiredCount)
    .filter((startsAt) => startsAt < resultTime - 3.4)
    .map((startsAt, index) => {
      const type = types[index % types.length];
      return {
        id: `event-${index + 1}`,
        type,
        ...EVENT_COPY[type],
        announcesAt: startsAt - 1.5,
        startsAt,
        duration: 2.2 + random() * .35,
      };
    });
}

export interface ActiveRaceEvent {
  readonly event: ScheduledRaceEvent;
  readonly amount: number;
  readonly envelope: number;
}

export function activeRaceEventAt(events: readonly ScheduledRaceEvent[], elapsed: number): ActiveRaceEvent | null {
  const event = events.find((candidate) => elapsed >= candidate.startsAt && elapsed <= candidate.startsAt + candidate.duration);
  if (!event) return null;
  const amount = (elapsed - event.startsAt) / event.duration;
  if (event.rampSeconds !== undefined && event.rampSeconds > 0) {
    const ramp = Math.min(event.rampSeconds, event.duration / 2);
    const edge = Math.max(0, Math.min(1, (elapsed - event.startsAt) / ramp, (event.startsAt + event.duration - elapsed) / ramp));
    return { event, amount, envelope: edge * edge * (3 - 2 * edge) };
  }
  return { event, amount, envelope: Math.sin(Math.PI * amount) };
}

export interface LiveEventContext {
  readonly lapStarts: readonly (number | undefined)[];
  readonly leaderProgress: number;
  readonly someoneFinished: boolean;
  readonly presence?: Partial<Record<RaceEventType, { readonly occupied: boolean; readonly zoneIndex: number }>>;
}

export function eventZoneContains(zone: FieldEventZone, x: number, y: number): boolean {
  return zone.rect ? pointInFieldRect(x, y, zone.rect)
    : zone.circle ? Math.hypot(x - zone.circle.x, y - zone.circle.y) <= zone.circle.r : false;
}

/** 현재 점유 우선, 다음은 0.55초 내 접근. 같은 장치가 여러 곳이면 선두에 가까운 실제 구간. */
export function fieldEventPresence(zones: readonly FieldEventZone[], marbles: readonly { x: number; y: number; vx: number; vy: number; progress: number; finished: boolean; lap?: number }[]): NonNullable<LiveEventContext['presence']> {
  const result: NonNullable<LiveEventContext['presence']> = {};
  const scores = new Map<RaceEventType, number>();
  zones.forEach((zone, zoneIndex) => {
    for (const marble of marbles) {
      if (marble.finished) continue;
      if ((marble.lap ?? 1) < (zone.minLap ?? 1)) continue;
      const occupied = eventZoneContains(zone, marble.x, marble.y);
      if (!occupied && !eventZoneContains(zone, marble.x + marble.vx * .55, marble.y + marble.vy * .55)) continue;
      const score = (occupied ? 2 : 0) + marble.progress;
      if (score <= (scores.get(zone.type) ?? -Infinity)) continue;
      scores.set(zone.type, score);
      result[zone.type] = { occupied, zoneIndex };
    }
  });
  return result;
}

/** 선택만 시드로 정하고, 랩 앵커/진행도/간격 검사는 실제 물리 시뮬레이션에서 한다. */
export class LiveRaceEventScheduler {
  readonly events: ScheduledRaceEvent[] = [];
  private readonly waiting: ScheduledRaceEvent[];
  private pending: ScheduledRaceEvent | null = null;

  constructor(plans: readonly ScheduledRaceEvent[], private readonly minimumGap: number, private readonly lastStartAt: number, private readonly timeScale = 1) {
    this.waiting = [...plans];
  }

  advance(time: number, context: LiveEventContext): ActiveRaceEvent | null {
    if (this.pending && time + 1e-7 >= this.pending.startsAt) {
      const presence = context.presence?.[this.pending.type];
      const allowed = !context.someoneFinished && time <= this.lastStartAt && context.leaderProgress <= (this.pending.trigger?.maxProgress ?? 1);
      if (allowed && (!this.pending.trigger?.occupancy || presence?.occupied)) {
        this.events.push({ ...this.pending, ...(presence ? { focusZoneIndex: presence.zoneIndex } : {}) });
      } else if (allowed && this.pending.trigger?.occupancy) {
        // 이미 통과했으면 빈 장치를 켜지 않는다. 다음 실제 접근 기회만 기다린다.
        this.waiting.push(this.pending);
      }
      this.pending = null;
    }
    if (!this.pending && !context.someoneFinished && time <= this.lastStartAt) {
      const previous = this.events.at(-1);
      const earliest = previous ? previous.startsAt + Math.max(this.minimumGap, previous.duration + .5 * this.timeScale) : 0;
      const candidates = this.waiting.flatMap((plan) => {
        if (plan.trigger?.occupancy && !context.presence?.[plan.type]) return [];
        const anchor = plan.trigger?.lap ? context.lapStarts[plan.trigger.lap - 1] : 0;
        if (anchor === undefined) return [];
        const urgent = plan.trigger?.fireByProgress !== undefined && context.leaderProgress >= plan.trigger.fireByProgress;
        const lead = plan.trigger?.occupancy ? .55 : 2 * this.timeScale;
        const requested = plan.trigger?.occupancy ? Math.max(2.05, time + lead)
          : urgent ? Math.min(anchor + plan.startsAt, time + lead) : anchor + plan.startsAt;
        return [{ plan, anchor, start: Math.max(requested, earliest) }];
      }).sort((a, b) => a.start - b.start || a.plan.id.localeCompare(b.plan.id));
      for (const { plan, anchor, start } of candidates) {
        const deadline = Math.min(this.lastStartAt, anchor + (plan.latestStart ?? Infinity));
        if (context.leaderProgress > (plan.trigger?.maxProgress ?? 1) || time > deadline || start > deadline) {
          this.waiting.splice(this.waiting.indexOf(plan), 1);
          continue;
        }
        const lead = plan.trigger?.occupancy ? .55 : 2 * this.timeScale;
        if (time + 1e-7 < start - lead || context.leaderProgress < (plan.trigger?.minProgress ?? 0)) continue;
        const actualStart = Math.max(start, time + lead);
        if (actualStart > deadline + 1 / 60) continue;
        this.pending = { ...plan, announcesAt: time, startsAt: actualStart, latestStart: deadline };
        this.waiting.splice(this.waiting.indexOf(plan), 1);
        break;
      }
    }
    return activeRaceEventAt(this.events, time);
  }
}
